/**
 * WeeklyWebsiteReportCard — Einstellungen fuer den Sonntags-Website-Report
 * (Besucher 7/14/30 Tage, Klick-Quellen, Tracking-Link-Klicks per E-Mail).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, CalendarClock, Send, Save, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyWebsiteReportCard() {
  const [cfg, setCfg] = useState({ enabled: false, recipients: [], send_time: "17:00" });
  const [recipientsText, setRecipientsText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/website-weekly/settings`, { withCredentials: true })
      .then((r) => { setCfg(r.data); setRecipientsText((r.data.recipients || []).join(", ")); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/reports/website-weekly/settings`, {
        ...cfg,
        recipients: recipientsText.split(",").map((s) => s.trim()).filter(Boolean),
      }, { withCredentials: true });
      setCfg(r.data);
      setRecipientsText((r.data.recipients || []).join(", "));
      toast.success("Report-Einstellungen gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setSaving(false);
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/reports/website-weekly/send`, {}, { withCredentials: true });
      if (r.data.sent > 0) toast.success(`Report an ${r.data.sent} Empfänger gesendet (${(r.data.recipients || []).join(", ")})`);
      else toast.error(r.data.error || (r.data.errors || []).join("; ") || "Versand fehlgeschlagen — SMTP konfiguriert?");
    } catch (e) { toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen"); }
    setSending(false);
  };

  if (loading) return null;

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="weekly-website-report-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <CalendarClock className="w-4 h-4" /> Wochen-Report Websites (jeden Sonntag)
        </CardTitle>
        <p className="text-xs text-[var(--studio-text-muted)]">
          Besucherzahlen aller Websites (7/14/30 Tage, pro Domain getrennt — auch app.strongfit.ch),
          Klick-Quellen und Tracking-Link-Klicks der letzten 7 Tage als übersichtliche E-Mail.
          Voraussetzung: SMTP oben konfiguriert.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))}
            data-testid="weekly-report-enabled-switch" />
          <span className="text-sm">{cfg.enabled ? "Aktiv — wird jeden Sonntag versendet" : "Ausgeschaltet"}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Empfänger (Komma-getrennt; leer = alle Admins)</Label>
            <Input className="h-8 text-sm" placeholder="alex@invictax.ch, info@strongfit.ch"
              value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)}
              data-testid="weekly-report-recipients-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Uhrzeit (UTC)</Label>
            <Input className="h-8 text-sm" type="time" value={cfg.send_time}
              onChange={(e) => setCfg((c) => ({ ...c, send_time: e.target.value }))}
              data-testid="weekly-report-time-input" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" className="h-8 gap-1" onClick={save} disabled={saving} data-testid="weekly-report-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={sendNow} disabled={sending} data-testid="weekly-report-send-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Jetzt senden (Testlauf)
          </Button>
          <Button size="sm" variant="ghost" className="h-8 gap-1" data-testid="weekly-report-preview-btn"
            onClick={() => window.open(`${API}/reports/website-weekly/preview`, "_blank")}>
            <Eye className="w-3.5 h-3.5" /> Vorschau ansehen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
